import type BrowserInfo from '../interfaces/BrowserInfo';
import type DeviceInfo from '../interfaces/DeviceInfo';
import { generateUUID } from '../utils/utils';

/**
 * Generates and returns device information.
 *
 * @return {DeviceInfo} An object containing culture, deviceID, deviceName,
 *                      deviceType, offSetClientTime, and operatingSystem.
 */
export function generateDeviceInfo(): DeviceInfo {
  const info = getDeviceInfo();
  const culture = navigator.language ? navigator.language.substring(0, 2).toLowerCase() : 'en';
  return {
    culture: culture,
    deviceID: generateUUID(),
    deviceName: info.browser,
    deviceType: 1002, // BROWSER
    offSetClientTime: new Date().getTimezoneOffset(),
    operatingSystem: info.os,
  };
}

/**
 * Retrieves information about the user's device, including browser and operating system.
 *
 * @return {BrowserInfo} An object containing the browser and operating system info.
 */
function getDeviceInfo(): BrowserInfo {
  const unknown = '-';

  const nAgt = navigator.userAgent;
  let browser = unknown;
  let nameOffset: number;
  let verOffset: number;

  if (nAgt.indexOf('Opera') !== -1 || nAgt.indexOf('OPR') !== -1) {
    browser = 'Opera';
  } else if (nAgt.indexOf('Edge') !== -1) {
    browser = 'Microsoft Legacy Edge';
  } else if (nAgt.indexOf('Edg') !== -1) {
    browser = 'Microsoft Edge';
  } else if (nAgt.indexOf('MSIE') !== -1 || nAgt.indexOf('Trident/') !== -1) {
    browser = 'Microsoft Internet Explorer';
  } else if (nAgt.indexOf('Chrome') !== -1) {
    browser = 'Chrome';
  } else if (nAgt.indexOf('Safari') !== -1) {
    browser = 'Safari';
  } else if (nAgt.indexOf('Firefox') !== -1) {
    browser = 'Firefox';
  } else {
    // If the browser is not identified, try to extract it from the User-Agent
    nameOffset = nAgt.lastIndexOf(' ') + 1;
    verOffset = nAgt.lastIndexOf('/');
    if (nameOffset < verOffset) {
      browser = nAgt.substring(nameOffset, verOffset);
      if (browser.toLowerCase() === browser.toUpperCase() || !browser) {
        browser = unknown;
      }
    } else {
      browser = unknown;
    }
  }

  // Operating system
  let os = unknown;
  const clientStrings = [
    {
      r: /(Windows 10.0|Windows NT 10.0|Windows 8.1|Windows NT 6.3|Windows 8|Windows NT 6.2|Windows 7|Windows NT 6.1|Windows Vista|Windows NT 6.0|Windows Server 2003|Windows NT 5.2|Windows XP|Windows NT 5.1|Windows 2000|Windows NT 5.0|Windows ME|Win 9x 4.90|Windows 98|Win98|Windows 95|Win95|Windows_95|Windows NT 4.0|WinNT4.0|WinNT|Windows NT|Windows CE|Win16)/,
      s: 'Windows',
    },
    { r: /Android/, s: 'Android' },
    { r: /OpenBSD/, s: 'Open BSD' },
    { r: /SunOS/, s: 'Sun OS' },
    { r: /CrOS/, s: 'Chrome OS' },
    { r: /(Linux|X11(?!.*CrOS))/, s: 'Linux' },
    { r: /(iPhone|iPad|iPod)/, s: 'iOS' },
    { r: /Mac OS X/, s: 'Mac OS X' },
    { r: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh)/, s: 'Mac OS' },
    { r: /QNX/, s: 'QNX' },
    { r: /UNIX/, s: 'UNIX' },
    { r: /BeOS/, s: 'BeOS' },
    { r: /OS\/2/, s: 'OS/2' },
    {
      r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/,
      s: 'Search Bot',
    },
  ];

  for (const cs of clientStrings) {
    if (cs.r.test(nAgt)) {
      os = cs.s;
      break;
    }
  }

  return {
    browser,
    os,
  };
}
